import React, { useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { IconButton, InputAdornment } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Form, FormInput, FormButton, Button } from '../../../Common';
import { SignInData, signInSchema, signInDefaultValues } from './SignInForm.helper';
import './SignInForm.css';

export type SignInFormProps = {
  onSubmit?: (data: SignInData) => Promise<void> | void;
  onForgotPassword?: () => void;
  className?: string;
};

const SignInForm: React.FC<SignInFormProps> = ({
  onSubmit,
  onForgotPassword,
  className = '',
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };
  
  const handleSubmit = async (data: SignInData) => {
    try {
      await onSubmit?.(data);
      // Note: Don't reset SignIn form - user will be redirected after successful login
    } catch (error) {
      // Don't clear form on error - let user retry
      console.error('Sign-in failed:', error);
    }
  };

  return (
    <div className={`signin-form-container ${className}`}>
      <div className="signin-title-section">
        <h1 className="signin-title">Sign In</h1>
        <p className="signin-subtitle">
          Please enter your account credentials to sign in.
        </p>
      </div>

      <div className="signin-form-section">
        <Form
          onSubmit={handleSubmit}
          className="signin-form-fields"
          defaultValues={signInDefaultValues}
          resolver={yupResolver(signInSchema)}
        >
          <FormInput
            name="email"
            placeholder="Email address"
            type="email"
            required
            fullWidth
          />

          <FormInput
            name="password"
            placeholder="Password"
            type={showPassword ? 'text' : 'password'}
            required
            fullWidth
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={togglePasswordVisibility}
                      edge="end"
                      className="password-toggle-button"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />

          <FormButton type="submit" primary size="full">
            Sign In
          </FormButton>
        </Form>

        <Button
          type="button"
          onClick={onForgotPassword}
          primary={false}
          bordered={false}
          size="full"
        >
          Forgot password
        </Button>
      </div>
    </div>
  );
};

export default SignInForm;
